import { useState, useCallback, useMemo, useEffect } from 'react';
import type { I18nConfig } from '../schema';
import {
    DEFAULT_ELEMENT_EN_STRINGS,
    DEFAULT_ELEMENT_VI_STRINGS,
    mergeRegisteredElementI18nBundles,
    getRegisteredElementI18nBundleCount,
    normalizeContentLocale,
    SUPPORTED_CONTENT_LOCALES,
} from '../schema/i18n';

let defaultI18nConfigCache: I18nConfig | null = null;
let defaultI18nConfigCacheRegistrationCount = -1;

/**
 * Default i18n configuration shape (built with registered element bundles).
 */
export function buildDefaultI18nConfig(): I18nConfig {
    const ext = mergeRegisteredElementI18nBundles();
    return {
        defaultLanguage: 'en',
        languages: {
            en: {
                ...DEFAULT_ELEMENT_EN_STRINGS,
                ...ext.en,
            // Actions
            submit: 'Submit',
            submitAnswers: 'Submit Answers',
            submitQuiz: 'Submit Quiz',
            retry: 'Try Again',
            next: 'Next',
            previous: 'Previous',
            hint: 'Show Hint',
            // Feedback
            correct: 'Correct!',
            incorrect: 'Incorrect',
            partiallyCorrect: 'Partially Correct',
            congratulations: '🎉 Congratulations!',
            keepPracticing: '📚 Keep Practicing',
            quizComplete: 'Quiz Complete',
            // Status
            timeUp: 'Time is up!',
            selectAnswer: 'Please select an answer',
            answerAllQuestions: 'Answer all questions to submit',
            answerAtLeastOneQuestion: 'Answer at least one question to submit',
            loading: 'Loading...',
            error: 'An error occurred',
            // Labels
            progress: 'Progress',
            answered: 'answered',
            question: 'Question',
            of: 'of',
            score: 'Score',
            points: 'Points',
            timeSpent: 'Time spent',
            minutes: 'm',
            seconds: 's',
            attemptsLeft: 'attempts left',
            noQuestions: 'No questions available',
            noQuizData: 'No quiz data',
            noQuizItems: 'No quiz items',
            // Editor labels
            title: 'Title',
            description: 'Description',
            instructions: 'Instructions',
            questions: 'Questions',
            quizItems: 'Quiz Items',
            addQuestion: 'Add Question',
            addItem: 'Add Item',
            questionSetDetails: 'Question Set Details',
            quizDetails: 'Quiz Details',
            noQuestionsYet: 'No questions yet. Add your first question to get started.',
            noItemsYet: 'No items yet. Add questions or content blocks to your quiz.',
            multipleChoice: 'Multiple Choice',
            textBlock: 'Text Block',
            gradeable: 'Gradeable',
            pointsLabel: 'Points:',
            untitledQuestion: 'Untitled question',
            emptyText: 'Empty text',
            unknownItem: 'Unknown item',
            unknownElementType: 'Unknown element type',
            // Config editor (ContentEditor settings)
            cfg_sectionLayout: 'Layout',
            cfg_sectionLayoutDescLessonMcq:
                'Content vs questions placement and question list display',
            cfg_sectionLayoutDescEssay:
                'Arrangement, split ratio, and minimum height for panels',
            cfg_sectionLayoutDescImageHotspot:
                'How lesson content and questions are shown in the player',
            cfg_sectionLayoutDescQuestionSet:
                'Page vs questions placement and multi-question display',
            cfg_sectionLayoutDescSpeaking:
                'Page vs speaking prompts placement and multi-prompt display',
            cfg_sectionLayoutDescMcqSet: 'How multiple questions appear in the player',
            cfg_sectionBehavior: 'Behavior',
            cfg_sectionBehaviorDesc: 'Control how learners interact with this content',
            cfg_sectionDisplay: 'Display',
            cfg_sectionDisplayDesc:
                'Show or hide title and description fields in the editor',
            cfg_sectionScoring: 'Scoring',
            cfg_sectionScoringDesc: 'Configure scoring and grading options',
            cfg_sectionTimer: 'Timer',
            cfg_sectionTimerDesc: 'Time limit settings',
            cfg_sectionTheme: 'Theme',
            cfg_sectionThemeDesc: 'Visual appearance customization',
            cfg_sectionI18n: 'Internationalization',
            cfg_sectionI18nDesc: 'Language and localization settings',
            cfg_fieldPanelArrangement: 'Panel arrangement',
            cfg_fieldPanelArrangementDescLessonMcq:
                'Side by side or stacked content and questions',
            cfg_fieldPanelArrangementDescEssay:
                'Side by side or stacked content and essay area',
            cfg_fieldPanelArrangementDescQuestionSet:
                'Side by side on wide screens; stacks vertically on small screens',
            cfg_fieldPanelArrangementDescSpeaking:
                'Side by side or stacked when lesson page has content',
            cfg_fieldLayoutSplit: 'Layout split',
            cfg_fieldLayoutSplitDesc: 'Ratio when arrangement is horizontal',
            cfg_fieldQuestionDisplay: 'Question display',
            cfg_fieldQuestionDisplayDesc: 'All questions on one page or one at a time',
            cfg_fieldMinHeight: 'Min Height',
            cfg_fieldMinHeightDesc: 'Minimum panel height (e.g. 500px)',
            cfg_fieldPlayerLayout: 'Player layout',
            cfg_fieldPlayerLayoutDesc: 'Tabs, stacked, or side-by-side',
            cfg_fieldInlineQuestionNav: 'Inline question navigation',
            cfg_fieldInlineQuestionNavDesc:
                'Keep jump-to-question controls inside the activity instead of the player header (when the host provides a header slot)',
            cfg_fieldPromptDisplay: 'Prompt display',
            cfg_fieldPromptDisplayDesc: 'All prompts on one page or one at a time',
            cfg_fieldQuestionDisplayMcqSet: 'Question display',
            cfg_fieldQuestionDisplayMcqSetDesc: 'Single page or multi-page navigation',
            cfg_allowRetry: 'Allow Retry',
            cfg_allowRetryDesc: 'Let learners try again after incorrect answer',
            cfg_maxAttempts: 'Max Attempts',
            cfg_maxAttemptsDesc: 'Maximum number of attempts (0 = unlimited)',
            cfg_shuffleChoices: 'Shuffle Choices',
            cfg_shuffleChoicesDesc: 'Randomize choice order',
            cfg_showCorrectAnswer: 'Show Correct Answer',
            cfg_showCorrectAnswerDesc: 'Reveal correct answer after submission',
            cfg_showExplanation: 'Show Explanation',
            cfg_showExplanationDesc: 'Display explanation after submission',
            cfg_instantFeedback: 'Instant Feedback',
            cfg_instantFeedbackDesc: 'Show feedback immediately after selection',
            cfg_examMode: 'Exam Mode',
            cfg_examModeDesc: 'No feedback until completion',
            cfg_autoAdvance: 'Auto Advance',
            cfg_autoAdvanceDesc: 'Move to next content after completion',
            cfg_showTitle: 'Show Title',
            cfg_showTitleDesc: 'Display title field in content editor',
            cfg_showDescription: 'Show Description',
            cfg_showDescriptionDesc: 'Display description field in content editor',
            cfg_maxScore: 'Max Score',
            cfg_maxScoreDesc: 'Maximum points for this content',
            cfg_partialCredit: 'Partial Credit',
            cfg_partialCreditDesc: 'Award partial points for partially correct answers',
            cfg_penaltyPerWrong: 'Penalty per Wrong',
            cfg_penaltyPerWrongDesc: 'Points deducted for wrong answers',
            cfg_passingScore: 'Passing Score',
            cfg_passingScoreDesc: 'Score required to pass (0-1)',
            cfg_enableTimer: 'Enable Timer',
            cfg_enableTimerDesc: 'Enable countdown timer',
            cfg_durationSeconds: 'Duration (seconds)',
            cfg_durationSecondsDesc: 'Time limit in seconds',
            cfg_warningSeconds: 'Warning (seconds)',
            cfg_warningSecondsDesc: 'Show warning at this remaining time',
            cfg_autoSubmit: 'Auto Submit',
            cfg_autoSubmitDesc: 'Submit automatically when timer expires',
            cfg_primaryColor: 'Primary Color',
            cfg_primaryColorDesc: 'Main accent color',
            cfg_successColor: 'Success Color',
            cfg_successColorDesc: 'Color for correct/success states',
            cfg_errorColor: 'Error Color',
            cfg_errorColorDesc: 'Color for incorrect/error states',
            cfg_backgroundColor: 'Background Color',
            cfg_backgroundColorDesc: 'Background color',
            cfg_textColor: 'Text Color',
            cfg_textColorDesc: 'Primary text color',
            cfg_borderRadius: 'Border Radius',
            cfg_borderRadiusDesc: 'Rounded corners (e.g., 8px, 1rem)',
            cfg_fontFamily: 'Font Family',
            cfg_fontFamilyDesc: 'Font for content text',
            cfg_fontSize: 'Font Size',
            cfg_fontSizeDesc: 'Base font size',
            cfg_themePreview: 'Preview',
            cfg_themePreviewSample: 'Sample content text',
            cfg_optSplit5050: '50% / 50%',
            cfg_optSplit6040: '60% / 40%',
            cfg_optSplit4060: '40% / 60%',
            cfg_optSplit7030: '70% / 30%',
            cfg_optSplit3070: '30% / 70%',
            cfg_optPlayerTabs: 'Tabs (switch views)',
            cfg_optPlayerStacked: 'Vertical (stacked)',
            cfg_optPlayerSplit: 'Horizontal (side by side)',
            cfg_optPanelAuto: 'Auto (by screen size)',
            cfg_optPanelHorizontal: 'Horizontal (side by side)',
            cfg_optPanelVertical: 'Vertical (stacked)',
            cfg_optQuestionPageAll: 'Single page (scroll)',
            cfg_optQuestionPageMulti: 'Multi-page',
            cfg_fontSystem: 'System',
            cfg_sizeSmall: 'Small (14px)',
            cfg_sizeStandard: 'Standard (16px)',
            cfg_sizeComfortable: 'Comfortable (18px)',
            cfg_sizeLarge: 'Large (20px)',
            cfg_selectPlaceholderDefault: 'Default',
            cfg_defaultLanguage: 'Default Language',
            cfg_defaultLanguageDesc: 'Primary content language',
            cfg_langEnglish: 'English',
            cfg_langVietnamese: 'Vietnamese',
            contentTab: 'Content',
            questionsTab: 'Questions',
            configurationTab: 'Configuration',
            enterTitle: 'Enter title',
            instructionsOptional: 'Instructions (Optional)',
            enterInstructions: 'Enter instructions for the exercise',
            questionNavigation: 'Question navigation',
            previousPage: 'Previous page',
            nextPage: 'Next page',
            selectOrAddQuestion:
                'Select a question, a section, or add one to start editing.',
        },
        vi: {
            ...DEFAULT_ELEMENT_VI_STRINGS,
            ...ext.vi,
            // Actions
            submit: 'Gửi',
            submitAnswers: 'Gửi câu trả lời',
            submitQuiz: 'Nộp bài',
            retry: 'Thử lại',
            next: 'Tiếp theo',
            previous: 'Trước',
            hint: 'Xem gợi ý',
            // Feedback
            correct: 'Chính xác!',
            incorrect: 'Chưa đúng',
            partiallyCorrect: 'Đúng một phần',
            congratulations: '🎉 Chúc mừng!',
            keepPracticing: '📚 Tiếp tục luyện tập',
            quizComplete: 'Hoàn thành bài kiểm tra',
            // Status
            timeUp: 'Hết thời gian!',
            selectAnswer: 'Vui lòng chọn câu trả lời',
            answerAllQuestions: 'Trả lời tất cả câu hỏi để nộp bài',
            answerAtLeastOneQuestion: 'Trả lời ít nhất một câu hỏi để nộp bài',
            loading: 'Đang tải...',
            error: 'Đã xảy ra lỗi',
            // Labels
            progress: 'Tiến độ',
            answered: 'đã trả lời',
            question: 'Câu hỏi',
            of: 'trên',
            score: 'Điểm',
            points: 'Điểm',
            timeSpent: 'Thời gian',
            minutes: ' phút',
            seconds: ' giây',
            attemptsLeft: 'lần thử còn lại',
            noQuestions: 'Không có câu hỏi nào',
            noQuizData: 'Không có dữ liệu bài kiểm tra',
            noQuizItems: 'Không có mục nào trong bài kiểm tra',
            // Editor labels
            title: 'Tiêu đề',
            description: 'Mô tả',
            instructions: 'Hướng dẫn',
            questions: 'Câu hỏi',
            quizItems: 'Các mục',
            addQuestion: 'Thêm câu hỏi',
            addItem: 'Thêm mục',
            questionSetDetails: 'Chi tiết bộ câu hỏi',
            quizDetails: 'Chi tiết bài kiểm tra',
            noQuestionsYet: 'Chưa có câu hỏi nào. Thêm câu hỏi đầu tiên để bắt đầu.',
            noItemsYet: 'Chưa có mục nào. Thêm câu hỏi hoặc khối nội dung vào bài kiểm tra.',
            multipleChoice: 'Trắc nghiệm',
            textBlock: 'Khối văn bản',
            gradeable: 'Có điểm',
            pointsLabel: 'Điểm:',
            untitledQuestion: 'Câu hỏi chưa có tiêu đề',
            emptyText: 'Văn bản trống',
            unknownItem: 'Mục không xác định',
            unknownElementType: 'Loại phần tử không xác định',
            cfg_sectionLayout: 'Bố cục',
            cfg_sectionLayoutDescLessonMcq:
                'Vị trí nội dung và câu hỏi, hiển thị danh sách câu hỏi',
            cfg_sectionLayoutDescEssay:
                'Sắp xếp, tỷ lệ chia và chiều cao tối thiểu của khung',
            cfg_sectionLayoutDescImageHotspot:
                'Cách hiển thị nội dung bài và câu hỏi trong trình phát',
            cfg_sectionLayoutDescQuestionSet:
                'Vị trí trang và câu hỏi, hiển thị nhiều câu hỏi',
            cfg_sectionLayoutDescSpeaking:
                'Vị trí trang và lời nhắc nói, hiển thị nhiều lời nhắc',
            cfg_sectionLayoutDescMcqSet:
                'Cách hiển thị nhiều câu hỏi trong trình phát',
            cfg_sectionBehavior: 'Hành vi',
            cfg_sectionBehaviorDesc:
                'Điều khiển cách học viên tương tác với nội dung',
            cfg_sectionDisplay: 'Hiển thị',
            cfg_sectionDisplayDesc:
                'Hiện hoặc ẩn tiêu đề và mô tả trong trình chỉnh sửa',
            cfg_sectionScoring: 'Chấm điểm',
            cfg_sectionScoringDesc: 'Cấu hình điểm và chấm điểm',
            cfg_sectionTimer: 'Bấm giờ',
            cfg_sectionTimerDesc: 'Cài đặt giới hạn thời gian',
            cfg_sectionTheme: 'Giao diện',
            cfg_sectionThemeDesc: 'Tùy chỉnh giao diện hiển thị',
            cfg_sectionI18n: 'Đa ngôn ngữ',
            cfg_sectionI18nDesc: 'Ngôn ngữ và bản địa hóa',
            cfg_fieldPanelArrangement: 'Sắp xếp khung',
            cfg_fieldPanelArrangementDescLessonMcq:
                'Nội dung và câu hỏi cạnh nhau hoặc xếp chồng',
            cfg_fieldPanelArrangementDescEssay:
                'Nội dung và vùng viết cạnh nhau hoặc xếp chồng',
            cfg_fieldPanelArrangementDescQuestionSet:
                'Cạnh nhau trên màn hình lớn; xếp dọc trên màn hình nhỏ',
            cfg_fieldPanelArrangementDescSpeaking:
                'Cạnh nhau hoặc xếp chồng khi trang bài có nội dung',
            cfg_fieldLayoutSplit: 'Tỷ lệ chia',
            cfg_fieldLayoutSplitDesc: 'Tỷ lệ khi sắp xếp theo chiều ngang',
            cfg_fieldQuestionDisplay: 'Hiển thị câu hỏi',
            cfg_fieldQuestionDisplayDesc:
                'Tất cả câu hỏi trên một trang hoặc từng câu một',
            cfg_fieldMinHeight: 'Chiều cao tối thiểu',
            cfg_fieldMinHeightDesc: 'Chiều cao tối thiểu của khung (vd. 500px)',
            cfg_fieldPlayerLayout: 'Bố cục trình phát',
            cfg_fieldPlayerLayoutDesc: 'Thẻ, xếp dọc hoặc cạnh nhau',
            cfg_fieldInlineQuestionNav: 'Điều hướng câu hỏi trong hoạt động',
            cfg_fieldInlineQuestionNavDesc:
                'Giữ điều khiển nhảy câu trong hoạt động thay vì tiêu đề trình phát (khi có tiêu đề từ host)',
            cfg_fieldPromptDisplay: 'Hiển thị lời nhắc',
            cfg_fieldPromptDisplayDesc:
                'Tất cả lời nhắc trên một trang hoặc từng cái một',
            cfg_fieldQuestionDisplayMcqSet: 'Hiển thị câu hỏi',
            cfg_fieldQuestionDisplayMcqSetDesc: 'Một trang hoặc nhiều trang',
            cfg_allowRetry: 'Cho phép thử lại',
            cfg_allowRetryDesc: 'Cho phép làm lại sau câu trả lời sai',
            cfg_maxAttempts: 'Số lần thử tối đa',
            cfg_maxAttemptsDesc: 'Số lần thử tối đa (0 = không giới hạn)',
            cfg_shuffleChoices: 'Xáo trộn đáp án',
            cfg_shuffleChoicesDesc: 'Ngẫu nhiên thứ tự lựa chọn',
            cfg_showCorrectAnswer: 'Hiện đáp án đúng',
            cfg_showCorrectAnswerDesc: 'Hiển thị đáp án đúng sau khi nộp',
            cfg_showExplanation: 'Hiện giải thích',
            cfg_showExplanationDesc: 'Hiển thị giải thích sau khi nộp',
            cfg_instantFeedback: 'Phản hồi tức thì',
            cfg_instantFeedbackDesc: 'Hiện phản hồi ngay sau khi chọn',
            cfg_examMode: 'Chế độ thi',
            cfg_examModeDesc: 'Không có phản hồi cho đến khi hoàn thành',
            cfg_autoAdvance: 'Tự chuyển tiếp',
            cfg_autoAdvanceDesc: 'Chuyển sang nội dung tiếp theo sau khi xong',
            cfg_showTitle: 'Hiện tiêu đề',
            cfg_showTitleDesc: 'Hiển thị ô tiêu đề trong trình chỉnh sửa',
            cfg_showDescription: 'Hiện mô tả',
            cfg_showDescriptionDesc: 'Hiển thị ô mô tả trong trình chỉnh sửa',
            cfg_maxScore: 'Điểm tối đa',
            cfg_maxScoreDesc: 'Điểm tối đa cho nội dung này',
            cfg_partialCredit: 'Điểm một phần',
            cfg_partialCreditDesc: 'Cấp điểm khi trả lời đúng một phần',
            cfg_penaltyPerWrong: 'Trừ điểm mỗi sai',
            cfg_penaltyPerWrongDesc: 'Điểm trừ cho mỗi câu sai',
            cfg_passingScore: 'Điểm đạt',
            cfg_passingScoreDesc: 'Điểm cần để đạt (0–1)',
            cfg_enableTimer: 'Bật bấm giờ',
            cfg_enableTimerDesc: 'Bật đếm ngược thời gian',
            cfg_durationSeconds: 'Thời lượng (giây)',
            cfg_durationSecondsDesc: 'Giới hạn thời gian tính bằng giây',
            cfg_warningSeconds: 'Cảnh báo (giây)',
            cfg_warningSecondsDesc: 'Hiện cảnh báo khi còn lại thời gian này',
            cfg_autoSubmit: 'Tự động nộp',
            cfg_autoSubmitDesc: 'Tự động nộp khi hết giờ',
            cfg_primaryColor: 'Màu chính',
            cfg_primaryColorDesc: 'Màu nhấn chính',
            cfg_successColor: 'Màu thành công',
            cfg_successColorDesc: 'Màu cho trạng thái đúng/thành công',
            cfg_errorColor: 'Màu lỗi',
            cfg_errorColorDesc: 'Màu cho trạng thái sai/lỗi',
            cfg_backgroundColor: 'Màu nền',
            cfg_backgroundColorDesc: 'Màu nền',
            cfg_textColor: 'Màu chữ',
            cfg_textColorDesc: 'Màu chữ chính',
            cfg_borderRadius: 'Bo góc',
            cfg_borderRadiusDesc: 'Độ bo góc (vd. 8px, 1rem)',
            cfg_fontFamily: 'Phông chữ',
            cfg_fontFamilyDesc: 'Phông chữ cho nội dung',
            cfg_fontSize: 'Cỡ chữ',
            cfg_fontSizeDesc: 'Cỡ chữ cơ bản',
            cfg_themePreview: 'Xem trước',
            cfg_themePreviewSample: 'Văn bản mẫu',
            cfg_optSplit5050: '50% / 50%',
            cfg_optSplit6040: '60% / 40%',
            cfg_optSplit4060: '40% / 60%',
            cfg_optSplit7030: '70% / 30%',
            cfg_optSplit3070: '30% / 70%',
            cfg_optPlayerTabs: 'Thẻ (chuyển chế độ xem)',
            cfg_optPlayerStacked: 'Dọc (xếp chồng)',
            cfg_optPlayerSplit: 'Ngang (cạnh nhau)',
            cfg_optPanelAuto: 'Tự động (theo kích thước màn hình)',
            cfg_optPanelHorizontal: 'Ngang (cạnh nhau)',
            cfg_optPanelVertical: 'Dọc (xếp chồng)',
            cfg_optQuestionPageAll: 'Một trang (cuộn)',
            cfg_optQuestionPageMulti: 'Nhiều trang',
            cfg_fontSystem: 'Hệ thống',
            cfg_sizeSmall: 'Nhỏ (14px)',
            cfg_sizeStandard: 'Tiêu chuẩn (16px)',
            cfg_sizeComfortable: 'Thoải mái (18px)',
            cfg_sizeLarge: 'Lớn (20px)',
            cfg_selectPlaceholderDefault: 'Mặc định',
            cfg_defaultLanguage: 'Ngôn ngữ mặc định',
            cfg_defaultLanguageDesc: 'Ngôn ngữ nội dung chính',
            cfg_langEnglish: 'Tiếng Anh',
            cfg_langVietnamese: 'Tiếng Việt',
            contentTab: 'Nội dung',
            questionsTab: 'Câu hỏi',
            configurationTab: 'Cấu hình',
            enterTitle: 'Nhập tiêu đề',
            instructionsOptional: 'Hướng dẫn (Tùy chọn)',
            enterInstructions: 'Nhập hướng dẫn cho bài tập',
            questionNavigation: 'Điều hướng câu hỏi',
            previousPage: 'Trang trước',
            nextPage: 'Trang tiếp',
            selectOrAddQuestion:
                'Chọn câu hỏi, mục, hoặc thêm mới để bắt đầu chỉnh sửa.',
        },
    },
};
}

export function getDefaultI18nConfig(): I18nConfig {
    const n = getRegisteredElementI18nBundleCount();
    if (defaultI18nConfigCache && defaultI18nConfigCacheRegistrationCount === n) {
        return defaultI18nConfigCache;
    }
    defaultI18nConfigCacheRegistrationCount = n;
    defaultI18nConfigCache = buildDefaultI18nConfig();
    return defaultI18nConfigCache;
}

/**
 * Restrict to supported locales (en/vi), merge onto app defaults, coerce default language.
 */
export function sanitizeI18nConfig(config: I18nConfig): I18nConfig {
    const defaultLanguage = normalizeContentLocale(config.defaultLanguage);
    const languages: Record<string, Record<string, string>> = {};

    for (const code of SUPPORTED_CONTENT_LOCALES) {
        languages[code] = {
            ...(getDefaultI18nConfig().languages[code] ?? {}),
            ...(config.languages[code] ?? {}),
        };
    }

    return { defaultLanguage, languages };
}

export interface UseLocaleOptions {
    config?: I18nConfig;
    initialLanguage?: string;
}

export interface UseLocaleReturn {
    // Current state
    language: string;
    languages: string[];

    // Translation function
    t: (key: string, fallback?: string) => string;

    // Actions
    setLanguage: (language: string) => void;

    // Get all translations for current language
    translations: Record<string, string>;
}

/**
 * Deep merge languages - merges translations within each language
 */
function mergeLanguages(
    defaultLangs: Record<string, Record<string, string>>,
    customLangs?: Record<string, Record<string, string>>
): Record<string, Record<string, string>> {
    if (!customLangs) return defaultLangs;

    const result: Record<string, Record<string, string>> = { ...defaultLangs };

    // Merge each language's translations
    for (const lang of Object.keys(customLangs)) {
        result[lang] = {
            ...(defaultLangs[lang] ?? {}), // Start with default translations for this language
            ...customLangs[lang], // Override with custom translations
        };
    }

    return result;
}

/**
 * Hook for content localization
 */
export function useLocale(options: UseLocaleOptions = {}): UseLocaleReturn {
    const { config: configOption, initialLanguage } = options;

    const mergedConfig = useMemo(() => {
        const base = getDefaultI18nConfig();
        const config = configOption ?? base;
        const raw: I18nConfig = {
            defaultLanguage: normalizeContentLocale(
                initialLanguage ?? config?.defaultLanguage ?? base.defaultLanguage
            ),
            languages: mergeLanguages(base.languages, config?.languages),
        };
        return sanitizeI18nConfig(raw);
    }, [configOption, initialLanguage]);

    const effectiveInitialLanguage = normalizeContentLocale(
        initialLanguage ?? configOption?.defaultLanguage ?? mergedConfig.defaultLanguage
    );

    // Current language state
    const [language, setLanguageState] = useState<string>(effectiveInitialLanguage);

    // Sync when host passes `initialLanguage` or config default changes (e.g. embed `lang=` param).
    useEffect(() => {
        const next = normalizeContentLocale(
            initialLanguage ?? configOption?.defaultLanguage ?? mergedConfig.defaultLanguage
        );
        setLanguageState((prev) => (prev === next ? prev : next));
    }, [initialLanguage, configOption?.defaultLanguage, mergedConfig.defaultLanguage]);

    // Available languages
    const languages = useMemo(() => {
        return Object.keys(mergedConfig.languages);
    }, [mergedConfig.languages]);

    // Current translations
    const translations = useMemo(() => {
        const lang = normalizeContentLocale(language);
        return (
            mergedConfig.languages[lang] ??
            mergedConfig.languages[mergedConfig.defaultLanguage] ??
            {}
        );
    }, [language, mergedConfig]);

    // Translation function
    const t = useCallback(
        (key: string, fallback?: string): string => {
            // Try current language
            if (translations[key]) {
                return translations[key];
            }

            // Try default language
            const defaultTranslations = mergedConfig.languages[mergedConfig.defaultLanguage];
            if (defaultTranslations?.[key]) {
                return defaultTranslations[key];
            }

            // Return fallback or key
            return fallback ?? key;
        },
        [translations, mergedConfig]
    );

    // Set language
    const setLanguage = useCallback(
        (newLanguage: string) => {
            const normalized = normalizeContentLocale(newLanguage);
            if (mergedConfig.languages[normalized]) {
                setLanguageState(normalized);
            } else {
                console.warn(
                    `Language "${newLanguage}" is not available. Available languages: ${languages.join(', ')}`
                );
            }
        },
        [mergedConfig.languages, languages]
    );

    return {
        language: normalizeContentLocale(language),
        languages,
        t,
        setLanguage,
        translations,
    };
}

export interface UseElementLocaleOptions {
    /** Content-type or host `config.i18n` */
    parentConfig?: I18nConfig;
    /** Per-element `schema.i18n` overrides */
    elementI18n?: I18nConfig;
    initialLanguage?: string;
}

/**
 * Merge parent content i18n with optional per-element strings; same language as content config.
 */
export function useElementLocale(options: UseElementLocaleOptions = {}): UseLocaleReturn {
    const { parentConfig, elementI18n, initialLanguage } = options;

    const config = useMemo((): I18nConfig => {
        const base = getDefaultI18nConfig();
        const fromParent = parentConfig
            ? {
                  defaultLanguage:
                      parentConfig.defaultLanguage ?? base.defaultLanguage,
                  languages: mergeLanguages(
                      base.languages,
                      parentConfig.languages
                  ),
              }
            : base;

        const merged = elementI18n
            ? {
                  /** Element `schema.i18n` only adds per-language keys; content default stays authoritative. */
                  defaultLanguage: fromParent.defaultLanguage,
                  languages: mergeLanguages(fromParent.languages, elementI18n.languages),
              }
            : fromParent;

        return sanitizeI18nConfig(merged);
    }, [parentConfig, elementI18n]);

    return useLocale({ config, initialLanguage });
}

/**
 * Create a translation function from config
 */
export function createTranslator(
    config: I18nConfig,
    language?: string
): (key: string, fallback?: string) => string {
    const sanitized = sanitizeI18nConfig({
        defaultLanguage: config.defaultLanguage,
        languages: mergeLanguages(getDefaultI18nConfig().languages, config.languages),
    });
    const currentLanguage = normalizeContentLocale(language ?? sanitized.defaultLanguage);
    const translations =
        sanitized.languages[currentLanguage] ??
        sanitized.languages[sanitized.defaultLanguage] ??
        {};
    const defaultTranslations = sanitized.languages[sanitized.defaultLanguage] ?? {};

    return (key: string, fallback?: string): string => {
        return translations[key] ?? defaultTranslations[key] ?? fallback ?? key;
    };
}
